import { useEffect, useState } from 'react'

import type { Module } from 'ag-grid-community'
import { ModuleRegistry } from 'ag-grid-community'

import { registerAgGridLicense } from '@b-stock/fe-core/ag-grid'

let agGridLicensePromise: Promise<void> | null = null
let isAgGridLicenseReady = false

// Test-only: clears the module-scope singleton so each test observes its
// own registration lifecycle instead of whatever a prior test already
// resolved.
export const __resetAgGridLicenseForTests = (): void => {
  agGridLicensePromise = null
  isAgGridLicenseReady = false
}

function ensureAgGridLicense(): Promise<void> {
  if (!agGridLicensePromise) {
    agGridLicensePromise = Promise.resolve(registerAgGridLicense()).then(() => {
      isAgGridLicenseReady = true
    })
  }
  return agGridLicensePromise
}

/**
 * Call at module scope in every file that renders <AgGridReact>, in place of
 * ModuleRegistry.registerModules([...]) + void registerAgGridLicense(). Each
 * call site passes its own module list; license registration is a singleton
 * kicked off once no matter how many grid files import this.
 */
export function registerAgGridModules(modules: Module[]): void {
  ModuleRegistry.registerModules(modules)
  void ensureAgGridLicense()
}

/**
 * Gates rendering of <AgGridReact>. Returns true once the license
 * registration singleton has resolved. AG Grid validates its license key
 * synchronously at grid-creation time, which can otherwise run before an
 * unawaited registerAgGridLicense() resolves (esp. during SSR hydration).
 */
export function useAgGridLicenseReady(): boolean {
  const [ready, setReady] = useState(isAgGridLicenseReady)

  useEffect(() => {
    if (ready) return
    let cancelled = false
    void ensureAgGridLicense().then(() => {
      if (!cancelled) setReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [ready])

  return ready
}
